import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getISODay,
  isAfter,
  isSameMonth,
  startOfDay,
  startOfMonth,
} from "date-fns";
import { supabase } from "../../lib/supabase";
import { cn } from "../../lib/cn";
import { GRADIENT } from "../../components/ProfileAvatar";
import type { Goal } from "../../lib/database.types";

const MOST = "oklch(0.94 0.035 215)";
const SOME = "oklch(0.97 0.015 215)";

const iso = (date: Date) => format(date, "yyyy-MM-dd");

/** p = completed ÷ total goals that day. Null means a future day. */
function dayStyle(p: number | null): { background: string; color: string } {
  if (p === null) return { background: "transparent", color: "#8a8a94" };
  if (p >= 1) return { background: GRADIENT, color: "#0a0a0b" };
  if (p >= 0.5) return { background: MOST, color: "#0a0a0b" };
  if (p > 0) return { background: SOME, color: "#56565e" };
  return { background: "#f3f3f5", color: "#8a8a94" };
}

/**
 * A month of daily completion for one person, per the Profile tab handoff (6a).
 *
 * Completion is the share of that day's live daily goals that were done. Days
 * before they joined count as missed; days still to come are left bare.
 */
export function MonthCalendar({
  personId,
  goals,
  joinedAt,
}: {
  personId: string;
  goals: Goal[];
  joinedAt: string;
}) {
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const today = startOfDay(new Date());
  const joined = startOfDay(new Date(joinedAt));

  const first = month;
  const last = endOfMonth(month);

  const periods = useQuery({
    queryKey: ["month-periods", personId, iso(first)],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("goal_periods")
        .select("goal_id, period_start, is_complete")
        .eq("owner_id", personId)
        .eq("cadence", "daily")
        .eq("is_complete", true)
        .gte("period_start", iso(first))
        .lte("period_start", iso(last));
      if (error) throw error;
      return data;
    },
  });

  const daily = goals.filter((goal) => goal.cadence === "daily");
  const completedOn = new Map<string, number>();
  for (const row of periods.data ?? []) {
    completedOn.set(
      row.period_start,
      (completedOn.get(row.period_start) ?? 0) + 1,
    );
  }

  function completion(day: Date): number | null {
    if (isAfter(day, today)) return null;
    if (isAfter(joined, day)) return 0;
    const key = iso(day);
    const done = completedOn.get(key) ?? 0;
    // Archived goals have no archive date, so only live ones count — but a day
    // never claims more completions than goals.
    const live = daily.filter(
      (goal) => !goal.is_archived && goal.starts_on <= key,
    ).length;
    const total = Math.max(live, done);
    return total === 0 ? 0 : done / total;
  }

  const days = eachDayOfInterval({ start: first, end: last });
  const leading = getISODay(first) - 1; // weeks start on Monday
  const canGoBack = isAfter(first, startOfMonth(joined));

  return (
    <section
      aria-label="Monthly progress"
      className="mt-auto flex flex-col gap-4 rounded-3xl border border-[#ececef] bg-white px-4 py-5"
    >
      <div className="flex items-center justify-between px-1">
        <h2 className="font-[inherit] text-lg font-semibold tracking-[-0.02em]">
          {format(month, "MMMM")}
        </h2>
        <div className="flex gap-2 text-[#56565e]">
          <ChevronButton
            label="Previous month"
            disabled={!canGoBack}
            onClick={() => setMonth((value) => addMonths(value, -1))}
            path="m15 18-6-6 6-6"
          />
          <ChevronButton
            label="Next month"
            disabled={isSameMonth(month, today)}
            onClick={() => setMonth((value) => addMonths(value, 1))}
            path="m9 18 6-6-6-6"
          />
        </div>
      </div>

      <div className="grid grid-cols-7 justify-items-center gap-y-2.5">
        {["M", "T", "W", "T", "F", "S", "S"].map((label, index) => (
          <span key={index} className="text-xs text-[#8a8a94]">
            {label}
          </span>
        ))}

        {Array.from({ length: leading }, (_, index) => (
          <span key={`pad-${index}`} />
        ))}

        {days.map((day) => {
          const isToday = iso(day) === iso(today);
          return (
            // The cell is the full column wide and 44px tall, so the tap target
            // is bigger than the 42px circle inside it.
            <span
              key={iso(day)}
              className="flex h-11 w-full items-center justify-center"
            >
              <span
                className={cn(
                  "flex size-[42px] items-center justify-center rounded-full font-['Geist_Mono',ui-monospace,monospace] text-[13px]",
                  isToday && "shadow-[0_0_0_2px_#0a0a0b]",
                )}
                style={dayStyle(completion(day))}
              >
                {format(day, "d")}
              </span>
            </span>
          );
        })}
      </div>

      <ul className="flex items-center justify-center gap-4 border-t border-[#ececef] pt-3.5 text-xs text-[#8a8a94]">
        <Legend background={GRADIENT} label="All done" />
        <Legend background={MOST} label="Most" />
        <Legend background="#f3f3f5" label="Missed" />
      </ul>
    </section>
  );
}

function ChevronButton({
  label,
  path,
  disabled,
  onClick,
}: {
  label: string;
  path: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-8 items-center justify-center rounded-full border border-[#ececef] disabled:opacity-35"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d={path} />
      </svg>
    </button>
  );
}

function Legend({ background, label }: { background: string; label: string }) {
  return (
    <li className="flex items-center gap-1.5">
      <span
        aria-hidden
        className="size-2.5 rounded-full"
        style={{ background }}
      />
      {label}
    </li>
  );
}
